import { ProCard } from '@ant-design/pro-components'
import { FormInstance, TabsProps, Input, Radio, Select, Checkbox, theme, Form, Row, Col } from 'antd'
import { FormProviderProps } from 'antd/es/form/context'
import { useWatch } from 'antd/es/form/Form'
import { NamePath } from 'antd/es/form/interface'
import React, { useRef, useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useCtxFormulas } from '../../../../../features/formulas/queries/useCtxFormulas'
import { customReportGrammarSelector } from '../../../../../redux/config/customReport/selectors'
import {
  CustomReportSectionsType,
  CustomReportDataSection,
  CustomReportTextSection
} from '../../../../../redux/context/customReports/typesSection'
import { CustomReportVariable, RowType } from '../../../../../redux/context/customReports/typesVariable'
import CategoryBuilder, { CategoryBuilderHandle } from '../category/Builder/CategoryBuilder'
import ChartTypeSelect, { GraphType } from '../chart/ChartTypeSelect'
import Editor from '../editor/Editor'
import InfoboxSettings, { presetCheckboxes } from '../infobox/InfoboxSettings'
import { buildInfoboxCategories, buildInfoboxVariables, InfoboxCategoryArgs } from '../infobox/infoboxCategoryBuilder'
import { useCalculatedReport } from '../CalculatedReportContext'
import { InfoboxFormValues } from '../infobox/types'
import { getVariable } from '../utils'
import ParamsAndStylesTableAgGrid, { ParamsAndStylesTableHandle } from '../variable/ParamsAndStylesTableAgGrid'
import { getMinimumData } from './utils'
import { showErrorMessage } from '../../../../../utils/message'
import TranslateFields from '../../../../../components/Form/TranslateFields'
import VariableTable from '../variable/VariableTable'

const CustomReportSectionForm = ({
  form,
  section,
  setVariableDelete,
  handleSubmit,
  handleValuesChange,
  variableDelete
}: {
  form: FormInstance
  section: Partial<CustomReportSectionsType> | null
  handleSubmit: (newSection: Partial<CustomReportSectionsType>) => void
  handleValuesChange: ({ type }: Partial<CustomReportSectionsType>) => void
  setVariableDelete: (val: CustomReportVariable[]) => void
  variableDelete: CustomReportVariable[]
}) => {
  // Form content for step 2
  const {
    token: { boxShadowTertiary }
  } = theme.useToken()
  const { t } = useTranslation()
  const grammarRules = useSelector(customReportGrammarSelector)
  const { data: reportData } = useCalculatedReport()
  const companyArgument = reportData?.args?.find(arg => arg.type === 'company')
  const dimensionArgument = reportData?.args?.find(arg => arg.type === 'dimension')
  const { data: formulas = [] } = useCtxFormulas()
  const sectionType = useWatch('type', form)
  const switchRowsAndColumns = useWatch(['style', 'switchRowsAndColumns'], form)
  const variables = (useWatch('variables', form) || []) as CustomReportVariable[]
  const categoryBuilderRef = useRef<CategoryBuilderHandle>(null)
  const paramsTableRef = useRef<ParamsAndStylesTableHandle>(null)
  const [variableNamePath, setVariableNamePath] = useState<NamePath>()
  const [variableModalOpen, setVariableModalOpen] = useState(false)
  const getSectionCategories = (DataSection: Partial<CustomReportSectionsType> | null) => {
    if (!DataSection) return undefined

    // Check if this is a section type that has categories
    if (DataSection.type === 'table' || DataSection.type === 'graph' || DataSection.type === 'pie') {
      return (DataSection as CustomReportDataSection).categories
    }

    return undefined
  }

  const getNextKey = useMemo(
    () => (vars: (CustomReportVariable & { key: number })[]) => {
      const keys = vars.map(({ key }: { key: number }) => +key)
      return Math.max(...(keys.length > 0 ? keys : [0])) + 1
    },
    []
  )

  const addVariable = (
    newItem: Partial<CustomReportVariable & { key: string }>,
    currentVariables: CustomReportVariable[]
  ) => {
    const showEmptyRows = newItem.rowType === RowType.empty || newItem.rowType === RowType.title
    return {
      ...newItem,
      key: `${getNextKey(currentVariables as (CustomReportVariable & { key: number })[])}`,
      params: {
        showEmptyRows
      }
    }
  }

  const handleVariablesChange = useCallback(
    (updatedVariables: CustomReportVariable[]) => {
      form.setFieldValue('variables', updatedVariables)
    },
    [form]
  )

  const formTabs: TabsProps['items'] = [
    {
      key: '1',
      label: t('global:table-column', { count: 2 }),
      children: (
        <CategoryBuilder
          ref={categoryBuilderRef}
          initialData={getSectionCategories(section)}
          sectionType={sectionType}
          switchRowsAndColumns={switchRowsAndColumns}
        />
      )
    },
    {
      key: '2',
      label: t('global:table-row', { count: 3 }),
      children: (
        <VariableTable
          open={variableModalOpen}
          setOpen={val => setVariableModalOpen(val)}
          hanldeVariableDelete={val => {
            if (val.id) {
              setVariableDelete([...variableDelete, val])
            }
          }}
          setVariableNamePath={setVariableNamePath}
          variableNamePath={variableNamePath}
        />
      )
    },
    {
      key: '3',
      label: t('customReportPage:parametersAndStyles'),
      children: (
        <ParamsAndStylesTableAgGrid
          ref={paramsTableRef}
          sectionType={sectionType}
          variables={variables}
          onChange={handleVariablesChange}
        />
      )
    }
  ]

  const onFinish = (values: any) => {
    try {
      const { name, nameFi, nameEn, nameSv, type, style, variables: formVariables, params } = values

      if (type === 'infobox') {
        const infoboxValues = values.infobox as InfoboxFormValues
        if (
          infoboxValues.comparison === 'forecast' &&
          infoboxValues.dynamicScope === 'none' &&
          infoboxValues.companyScope?.some(c => c.id && !c.budgetingScenarioId)
        ) {
          showErrorMessage(t('global:required-field'))
          return
        }
        const infoboxArgs: InfoboxCategoryArgs = {
          companyArgumentId: companyArgument?.id,
          dimensionArgumentId: dimensionArgument?.id
        }
        const infoboxCategories = buildInfoboxCategories(infoboxValues, infoboxArgs, grammarRules)
        const infoboxVariables = buildInfoboxVariables(infoboxValues)

        const newSection: Partial<CustomReportSectionsType> = {
          nameFi,
          nameEn,
          nameSv,
          name,
          type,
          params,
          categories: infoboxCategories,
          variables: infoboxVariables as CustomReportVariable[]
        }

        handleSubmit(newSection)
        return
      }

      paramsTableRef.current?.stopEditing()
      // Validoi kategoriat
      const { valid } = categoryBuilderRef.current?.validate() || { valid: true, errors: [] }
      if (!valid) {
        // Näytä virheet
        return
      }

      // Hae kategoria data
      const categoryData = categoryBuilderRef.current?.getData() || []

      // Yhdistä lomakkeen tiedot
      const text = values.text && JSON.parse(values.text)
      const newSection: Partial<CustomReportSectionsType> = {
        nameFi,
        nameEn,
        nameSv,
        name,
        type,
        style,
        params,
        text,
        categories: categoryData,
        variables: formVariables?.map(getVariable) ?? []
      }

      handleSubmit(newSection)
    } catch (error) {
      showErrorMessage()
    }
  }

  const handleFormFinish: FormProviderProps['onFormFinish'] = (name, { values, forms }) => {
    // single
    if (name === 'customReportVariableForm') {
      const { sectionForm } = forms
      const currentFormVariables = sectionForm.getFieldValue('variables') || []
      if (variableNamePath) {
        // edit
        // Name change form doesn't have all the fields so we have to merge values, so other values doesnt disappear
        const oldValues = sectionForm.getFieldValue(variableNamePath)
        sectionForm.setFieldValue(variableNamePath as NamePath, { ...oldValues, ...values })
      } else {
        // uuden lisäys
        const currentVariables = [...currentFormVariables]
        currentVariables.push(addVariable(values, currentVariables))
        sectionForm.setFieldsValue({ variables: currentVariables })
      }
      setVariableModalOpen(false)
    }
    // multiple
    if (name === 'customReportVariableFormMultiple') {
      const { sectionForm } = forms
      const currentFormVariables = sectionForm.getFieldValue('variables') || []
      const currentVariables = [...currentFormVariables]

      const { ids, rowType } = values

      if (Array.isArray(ids)) {
        ids.forEach(id => {
          currentVariables.push(addVariable(getMinimumData(formulas, rowType, id), currentVariables))
        })
      }
      sectionForm.setFieldsValue({ variables: currentVariables })
      setVariableModalOpen(false)
    }
  }

  const containerStyles = {
    flexGrow: 1,
    ...(sectionType !== 'infobox' && {
      overflowY: 'scroll' as const,
      maxHeight: 'calc(100vh - 200px)'
    }),
    margin: '0px -14px',
    marginTop: '8px',
    padding: '0px 14px'
  } satisfies React.CSSProperties

  return (
    <div style={containerStyles}>
      <Form.Provider onFormFinish={handleFormFinish}>
        <Form
          name="sectionForm"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          onValuesChange={handleValuesChange}
          initialValues={{
            type: 'table',
            style: {
              showDataLabels: 'false',
              type: GraphType.column
            },
            params: { reallocate: true },
            infobox: {
              rowType: 'keyFigure',
              period: 'currentmonth',
              comparison: 'budget',
              dynamicScope: 'none',
              ...presetCheckboxes.sparkline
            }
          }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="name"
                label={t('customReportPage:title')}
                rules={[{ required: true, message: t('global:required-field') }]}
              >
                <Input data-cy="section-name-input" />
              </Form.Item>
            </Col>
            <Col span={16}>
              <TranslateFields inline />
            </Col>
          </Row>
          <Form.Item name="type" label={t('global:type')}>
            <Radio.Group data-cy="section-type-radio" buttonStyle="solid">
              <Radio value="table">{t(`dashboardPage:table`)}</Radio>
              <Radio value="graph">{t(`dashboardPage:chart`)}</Radio>
              <Radio value="pie">{t(`dashboardPage:pie`)}</Radio>
              <Radio value="text">{t('global:text')}</Radio>
              <Radio value="infobox">{t('customReportPage:infobox', 'Infoboxi')}</Radio>
            </Radio.Group>
          </Form.Item>
          {sectionType === 'text' && (
            <Form.Item name="text" label="">
              <Editor form={form} section={section as CustomReportTextSection} />
            </Form.Item>
          )}
          {sectionType === 'pie' && (
            <Form.Item name={['style', 'showDataLabels']} label={t('customReportPage:graphValues')}>
              <Radio.Group buttonStyle="solid">
                <Radio value="true">{t('customReportPage:inSight')}</Radio>
                <Radio value="false">{t('customReportPage:hide')}</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          {sectionType === 'graph' && (
            <>
              <Form.Item name={['style', 'type']} label={t('customReportPage:graphType')}>
                <ChartTypeSelect style={{ width: 300 }} />
              </Form.Item>
              <Form.Item
                style={{ width: 300 }}
                name={['style', 'stacking']}
                label={t('customReportPage:stacking', 'Pinoaminen')}
              >
                <Select
                  allowClear
                  options={[
                    {
                      label: t('customReportPage:stackingNormal', 'Normaali'),
                      value: 'normal'
                    },
                    {
                      label: t('customReportPage:stackingPercent', 'Prosentti'),
                      value: 'percent'
                    }
                  ]}
                />
              </Form.Item>
              <Form.Item name={['style', 'showDataLabels']} label={t('customReportPage:graphValues')}>
                <Radio.Group buttonStyle="solid">
                  <Radio value="true">{t('customReportPage:inSight')}</Radio>
                  <Radio value="false">{t('customReportPage:hide')}</Radio>
                </Radio.Group>
              </Form.Item>
            </>
          )}
          {sectionType !== 'text' && sectionType !== 'infobox' && (
            <>
              <Form.Item noStyle name={['params', 'reallocate']} valuePropName="checked">
                <Checkbox>{t('customReportPage:calculateReallocation')}</Checkbox>
              </Form.Item>
              {sectionType !== 'pie' && (
                <Form.Item name={['style', 'switchRowsAndColumns']} valuePropName="checked">
                  <Checkbox>{t('customReportPage:switchRowsAndColumns')}</Checkbox>
                </Form.Item>
              )}
            </>
          )}

          {sectionType === 'infobox' && (
            <>
              <Form.Item noStyle name={['params', 'reallocate']} valuePropName="checked">
                <Checkbox>{t('customReportPage:calculateReallocation')}</Checkbox>
              </Form.Item>
              <InfoboxSettings form={form} />
            </>
          )}

          {/* Fixes variables behind tab */}
          {sectionType !== 'text' && sectionType !== 'infobox' && (
            <>
              <Form.Item hidden name="variables">
                <Input type="hidden" />
              </Form.Item>
              <ProCard
                data-cy="section-form-tabs"
                style={{ marginBottom: 24, boxShadow: boxShadowTertiary }}
                bordered
                tabs={{
                  type: 'line',
                  items: formTabs
                }}
              />
            </>
          )}
        </Form>
      </Form.Provider>
    </div>
  )
}

export default CustomReportSectionForm
